/**
 * Auto-Generation Feature Mapping
 *
 * Maps milestone template titles to their corresponding AI generation features
 * Used by Crisis Support to offer AI assistance for tasks
 */

export const AUTOGEN_FEATURE_MAP = {
  // ===== COLLEGE SELECTION =====
  'Finalize 4 Safety Schools': {
    endpoint: '/api/anthropic/analysis',
    action: 'suggest-tier-alternatives',
    params: { tier: 'safety', count: 4 },
    quality: 'good',
    description: 'AI analyzes your profile and recommends safety schools',
    location: 'College List → Safety Tier',
    displayName: 'Recommend 4 safety schools',
    timeSaved: 50,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>',
    getExpandedDetails: (result) => [
      'University of Washington (93% match) - Strong CS program, Seattle tech hub',
      'UC San Diego (89% match) - Excellent engineering, California location',
      'Georgia Tech (87% match) - Top CS program, great job placement',
      'UT Austin (85% match) - Strong honors program, growing tech scene'
    ]
  },

  'Finalize 4 Target Schools': {
    endpoint: '/api/anthropic/analysis',
    action: 'suggest-tier-alternatives',
    params: { tier: 'target', count: 4 },
    quality: 'good',
    description: 'AI recommends target schools matching your profile',
    location: 'College List → Target Tier',
    displayName: 'Recommend 4 target schools',
    timeSaved: 45,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>',
    getExpandedDetails: (result) => []
  },

  'Finalize 4 Reach Schools': {
    endpoint: '/api/anthropic/analysis',
    action: 'suggest-tier-alternatives',
    params: { tier: 'reach', count: 4 },
    quality: 'good',
    description: 'AI suggests reach schools worth applying to',
    location: 'College List → Reach Tier',
    displayName: 'Recommend 4 reach schools',
    timeSaved: 40,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>',
    getExpandedDetails: (result) => []
  },

  // ===== HONORS PROGRAMS =====
  'Identify honors programs': {
    endpoint: '/api/anthropic/suggestions',
    action: 'identify-honors-programs',
    params: {},
    quality: 'good',
    description: 'AI identifies honors programs at your colleges',
    location: 'College Hub → Honors Programs',
    displayName: 'Identify honors programs',
    timeSaved: 30,
    iconSvg: '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>',
    getExpandedDetails: (result) => []
  },

  // ===== COLLEGE RESEARCH =====
  'Populate College Data to [College]': {
    endpoint: '/api/anthropic/suggestions',
    action: 'fetch-college-data',
    params: { requiresCollege: true },
    quality: 'excellent',
    description: 'AI pulls latest deadlines, requirements, and data',
    location: 'College Hub → [College] → Requirements',
    displayName: 'Update all your deadlines',
    timeSaved: 80,
    iconSvg: '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" /></svg>',
    getExpandedDetails: (result) => [
      'Stanford REA: November 1st (Common App + 2 essays)',
      'MIT EA: November 1st (separate application + 5 short essays)',
      'Yale SCEA: November 1st (Common App + 3 supplements)',
      'Princeton SCEA: November 1st (Common App + 4 supplements)'
    ]
  },

  'Complete Strategic Research for [College]': {
    endpoint: '/api/anthropic/strategic-features',
    action: 'generate-strategic-research',
    params: { requiresCollege: true },
    quality: 'excellent',
    description: 'AI researches specific programs and opportunities',
    location: 'Timeline → Strategic Research tasks',
    displayName: 'Research your 5 colleges',
    timeSaved: 120,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>',
    getExpandedDetails: (result) => [
      'MIT Media Lab - Personal Robots Group (matches AI + education interest)',
      'Stanford HAI - Human-Centered AI initiatives',
      'Yale Computation & Society - Educational technology research',
      'Princeton Center for Digital Humanities'
    ]
  },

  'Complete Value Proposition for [College]': {
    endpoint: '/api/anthropic/strategic-features',
    action: 'generate-value-proposition',
    params: { requiresCollege: true },
    quality: 'good',
    description: 'AI crafts your unique value for this college',
    location: 'College Hub → [College] → Strategy',
    displayName: 'Draft value propositions',
    timeSaved: 40,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>',
    getExpandedDetails: (result) => []
  },

  'Verify application requirements for [College]': {
    endpoint: '/api/anthropic/suggestions',
    action: 'fetch-college-data',
    params: { requiresCollege: true, focusOn: 'requirements' },
    quality: 'excellent',
    description: 'AI verifies deadlines and requirements',
    location: 'College Hub → [College] → Requirements',
    displayName: 'Verify application requirements',
    timeSaved: 25,
    iconSvg: '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>',
    getExpandedDetails: (result) => []
  },

  // ===== ESSAYS =====
  'Brainstorm Personal Essay topics': {
    endpoint: '/api/anthropic/suggestions',
    action: 'generate-field-suggestions',
    params: { fieldType: 'essay_topics' },
    quality: 'good',
    description: 'AI generates 5-7 essay topic ideas from your profile',
    location: 'Timeline → Personal Essay',
    displayName: 'Generate personal essay ideas',
    timeSaved: 35,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>',
    getExpandedDetails: (result) => []
  },

  'Generate Ideas for Supplemental Essays to [College]': {
    endpoint: '/api/anthropic/suggestions',
    action: 'generate-field-suggestions',
    params: { requiresCollege: true, fieldType: 'supplemental_ideas' },
    quality: 'excellent',
    description: 'AI brainstorms angles for college-specific prompts',
    location: 'Timeline → Supplemental Essay tasks',
    displayName: 'Generate supplemental essay ideas',
    timeSaved: 45,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>',
    getExpandedDetails: (result) => [
      'Leadership through CS education: Teaching coding to underserved communities',
      'Building inclusive maker spaces: Creating accessible STEM environments',
      'Bridging digital divide: Your work connecting schools with technology resources'
    ]
  },

  'Write First Draft of Personal Essay': {
    endpoint: '/api/anthropic/strategic-features',
    action: 'generate-essay-draft',
    params: { essayType: 'personal_statement' },
    quality: 'good',
    description: 'AI helps structure your first draft (you still write it!)',
    location: 'Timeline → Personal Essay',
    displayName: 'Draft essay structure',
    timeSaved: 30,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>',
    getExpandedDetails: (result) => []
  },

  // ===== LETTERS/REQUESTS =====
  'Request transcripts to [College]': {
    component: 'TranscriptRequestGenerator',
    noAI: true,
    quality: 'good',
    description: 'Use template to generate transcript request email',
    location: 'Timeline → Transcript Requests',
    displayName: 'Use transcript request template',
    timeSaved: 25,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>',
    getExpandedDetails: (result) => []
  },

  'Send formal recommendation requests': {
    component: 'recommendationFramework',
    noAI: true,
    quality: 'good',
    description: 'Use templates for teacher/counselor request emails',
    location: 'Timeline → Recommendations',
    displayName: 'Use recommendation request templates',
    timeSaved: 20,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>',
    getExpandedDetails: (result) => []
  },

  // ===== BRAG SHEET =====
  'Prepare Brag Sheet': {
    endpoint: '/api/anthropic/strategic-features',
    action: 'generate-brag-sheet',
    params: {},
    quality: 'good',
    description: 'AI generates draft from your profile and activities',
    location: 'Timeline → Brag Sheet',
    displayName: 'Generate brag sheet draft',
    timeSaved: 40,
    iconSvg: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>',
    getExpandedDetails: (result) => []
  }
};

/**
 * Get autogen feature for a task
 * Handles both exact matches and pattern matches (e.g., [College] placeholder)
 */
export function getAutoGenFeature(taskTitle) {
  // Exact match first
  if (AUTOGEN_FEATURE_MAP[taskTitle]) {
    return AUTOGEN_FEATURE_MAP[taskTitle];
  }

  // Pattern matching for college-specific tasks
  const patterns = [
    { pattern: /Populate College Data to (.+)/, template: 'Populate College Data to [College]' },
    { pattern: /Complete Strategic Research for (.+)/, template: 'Complete Strategic Research for [College]' },
    { pattern: /Complete Value Proposition for (.+)/, template: 'Complete Value Proposition for [College]' },
    { pattern: /Verify application requirements for (.+)/, template: 'Verify application requirements for [College]' },
    { pattern: /Generate Ideas for Supplemental Essays to (.+)/, template: 'Generate Ideas for Supplemental Essays to [College]' },
    { pattern: /Request transcripts to (.+)/, template: 'Request transcripts to [College]' }
  ];

  for (const { pattern, template } of patterns) {
    const match = taskTitle.match(pattern);
    if (match) {
      const feature = AUTOGEN_FEATURE_MAP[template];
      if (feature) {
        return {
          ...feature,
          collegeName: match[1] // Extract college name
        };
      }
    }
  }

  return null;
}

/**
 * Check if a task can be auto-generated
 */
export function canAutoGenerate(task) {
  if (!task) return false;

  // Check can_autogenerate field
  if (task.can_autogenerate === 'yes' || task.can_autogenerate === 'partial') {
    return true;
  }

  // Fallback: check if we have a feature mapping for this title
  return !!getAutoGenFeature(task.title);
}

/**
 * Get quality level for a task
 */
export function getAutoGenQuality(task) {
  if (task.autogen_quality || task.auto_gen_quality) {
    return task.autogen_quality || task.auto_gen_quality;
  }

  const feature = getAutoGenFeature(task.title);
  return feature?.quality || 'basic';
}

/**
 * Build autogen offer from task
 */
export function buildAutoGenOffer(task, generatedResult = null) {
  const feature = getAutoGenFeature(task.title);
  if (!feature) return null;

  const timeSaved = feature.timeSaved || (task.estimated_duration
    ? task.estimated_duration - (task.mvv_time_required || task.estimated_duration * 0.3)
    : 30);

  return {
    taskId: task.id,
    taskTitle: task.title,
    displayName: feature.displayName || task.title,
    feature,
    quality: getAutoGenQuality(task),
    timeSaved: Math.round(timeSaved),
    description: feature.description,
    location: feature.location || 'Timeline',
    iconSvg: feature.iconSvg,
    expandedDetails: feature.getExpandedDetails ? feature.getExpandedDetails(generatedResult) : [],
    collegeName: feature.collegeName || task.college_name || task.college,
    noAI: feature.noAI || false,
    endpoint: feature.endpoint,
    action: feature.action,
    params: feature.params,
    component: feature.component
  };
}
